import { DateRangePicker } from "rsuite";
import { addDays, subDays } from "date-fns";
import { KeyboardEvent } from "react";
import type { TFunction } from "i18next";

import type { IPreDefinedRange } from "libs/design-system/src/types/date_range_picker";
import type { TCurrentTime } from "libs/design-system/src/types/time_picker";
import { getPaymentMethod } from "@unified-checkout/design-system";

import i18n from '@app/dashboard/tools/i18n';
import { getMinimumValidAmount } from "@app/dashboard/utils/getMinimumValidAmount";


export const BULK_SHEET_FORMATS_ALLOWED = ['xlsx', 'csv'];
export const NO_FILE = 'noFile';
export const BULK_INSTRUCTION_FILE_NAME = 'bulk-payment-link-instructions.pdf';
export const BULK_SHEET_TEMPLATE_FILE_NAME = 'bulk-payment-link-template.xlsx';
export const NOTIFICATION_TIEMOUT = 3;
export const MAX_NOTIFICATION_WIDTH = '30rem';
export const INPUT_DD_PROPS = {
  width: "fit-content",
  verticalPlacement: "auto",
  horizontalPlacement: "auto",
  maxHeight: '15rem'
} as const;

export const CARD_PAYMENT = "Card Payment";
export const DEFAULT_CURRENCY = "EGP";

export const MAX_AMOUNT = 99999999;
export const MIN_AMOUNT = 0.01;

export const DEFAULT_TIME = {
  hour: "11",
  minute: "59",
  ampm: "PM"
} as const;

export const AMOUNTS_FROM = {
  UPLOADED: "uploaded",
  FIXED: "fixed",
} as const;

export const FIELDS_IDS = {
  FIXED_AMOUNT: "fixed_amount",
  BULK_SHEET: "bulk_sheet",
  PAYMENT_METHODS: "payment_methods"
} as const;

export const BULK_PLS_SECTIONS_DIMENSIONS = {
  CUSTOMERS_SHEET: {
    dimensionsList: [
      { width: '9.5rem', height: '1.75rem', marginBottom: '-1rem' },
      { width: '14.5rem', height: '1.5rem' },
      { width: '100%', height: '11.25rem' },
      { width: '12rem', height: '1.5rem' },
    ]
  },
  PAYMENT: {
    dimensionsList: [
      { width: '8rem', height: '1.75rem' },
      { width: '28.75rem', height: '4.125rem' },
      { width: '11.5625rem', height: '3.25rem' },
      { width: '16.25rem', height: '1rem', marginBottom: '-0.75rem' },
    ]
  },
  RESTRICTIONS: {
    dimensionsList: [
      { width: '10rem', height: '1.75rem' },
      { width: '18rem', height: '1.625rem' },
    ]
  },
};


export type TUploadedFromValue = (typeof AMOUNTS_FROM)[keyof typeof AMOUNTS_FROM];
export type TBulkSubmissionData = {
  amountCents: string,
  useFixedAmount: boolean,
  bulkCustomers: ((string | number)[])[],
  paymentMethodsIds: string[],
  saveSelection: boolean,
  customExpDate: boolean,
  expiresAt: string
};

export const paymentLinksDatePresets: (t: TFunction) => IPreDefinedRange[] = (t) => {
  const TODAY = new Date();
  const TOMORROW = addDays(TODAY, 1);
  const IN_THREE_DAYS = addDays(TODAY, 3);
  const ONE_YEAR = 365;
  const TEN_YEARS = ONE_YEAR * 10;

  const { allowedRange } = DateRangePicker;

  return [
    {
      index: 1,
      name: 'today',
      disabled: false,
      label: t('innerLayout.paymentLinksCreationPages.sections.restrictions.datePicker.today'),
      selectionRange: [TODAY, TODAY],
      disabledRange: allowedRange(TODAY, TODAY),
    },
    {
      index: 2,
      name: 'tomorrow',
      disabled: false,
      label: t('innerLayout.paymentLinksCreationPages.sections.restrictions.datePicker.tomorrow'),
      selectionRange: [TOMORROW, TOMORROW],
      disabledRange: allowedRange(TOMORROW, TOMORROW),
    },
    {
      index: 3,
      name: 'dayAfterTomorrow',
      disabled: false,
      label: t('innerLayout.paymentLinksCreationPages.sections.restrictions.datePicker.inThreeDays'),
      selectionRange: [IN_THREE_DAYS, IN_THREE_DAYS],
      disabledRange: allowedRange(IN_THREE_DAYS, IN_THREE_DAYS),
    },
    {
      index: 4,
      name: 'custom',
      disabled: false,
      label: t(`innerLayout.paymentLinksCreationPages.sections.restrictions.datePicker.custom`),
      selectionRange: [TODAY, addDays(TODAY, TEN_YEARS)],
      disabledRange: allowedRange(subDays(TODAY, 0), addDays(TODAY, TEN_YEARS)),
      default: true,
    },
  ];
};

interface TTimePickerValue {
  hour: string;
  minute: string;
  ampm: string;
};

export function formatDateAndTime(
  date: Date,
  time: TCurrentTime
): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  let hour = time.hour ? parseInt(time.hour, 10) : 0;

  // 12 → 24 hour conversion
  if (time.ampm === 'PM' && hour !== 12) {
    hour += 12;
  }
  if (time.ampm === 'AM' && hour === 12) {
    hour = 0;
  }

  const hourStr = String(hour).padStart(2, '0');
  const minuteStr = time.minute ? time.minute : '00';

  return `${year}-${month}-${day}T${hourStr}:${minuteStr}:00`;
};

export const fixedAmountValidator = (amount: string, currency: string) => {
  let error = '';
  const minimumValidAmount = getMinimumValidAmount(currency);

  if (amount.trim().length === 0) {
    error = i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.fixedAmount.mandatory');
  } else if (Number(amount) > MAX_AMOUNT) {
    error = i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.fixedAmount.maxRange');
  } else if (Number(amount) < minimumValidAmount) {
    error = i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.fixedAmount.minRange');
  };

  return error;
};

export const badgeTextFormatter = (item: any) => {
  return `${getPaymentMethod(item.gatewayName).name} / ${item.integrationName || item.id}`;
};

export const remainingItemsBadgeTextFormatter = (items: any) => {
  const remainingItems =
    Object.values(items).map((item: any) => `${getPaymentMethod(item.gatewayName).name} / ${item.integrationName || item.id}`);
  return `${remainingItems.join(', ')}`;
};

export const reorderPaymentMethods = (paymentMethods: any, preSelectedMethods: any) => {
  if (!paymentMethods) return {};

  const preSelectedKeys = preSelectedMethods ? Object.keys(preSelectedMethods) : [];
  const allKeys = Object.keys(paymentMethods);

  // Order gateway keys: preselected first, then Card Payment, then rest
  const orderedKeys = [
    ...preSelectedKeys.filter(key => key === CARD_PAYMENT),
    ...preSelectedKeys.filter(key => key !== CARD_PAYMENT),
    ...allKeys.filter(key => !preSelectedKeys.includes(key) && key === CARD_PAYMENT),
    ...allKeys.filter(key => !preSelectedKeys.includes(key) && key !== CARD_PAYMENT)
  ];

  // Build ordered object with sorted integrations
  const orderedMethods: any = {};
  orderedKeys.forEach(key => {
    const methods = paymentMethods[key];
    if (key === CARD_PAYMENT) {
      orderedMethods[key] = methods; // Card Payment integrations first by default
    } else {
      orderedMethods[key] = methods;
    }
  });

  return orderedMethods;
};

export const preventExponentialNotationInput = (e: KeyboardEvent<HTMLInputElement>) => {
  if (['E', 'e'].includes(e.key)) {
    e.preventDefault();
  }
}